import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiCrosshair, FiEye, FiEyeOff, FiHelpCircle, FiLock, FiUnlock, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { formatPrice, patternMatchAnyLength } from './strategyTesterEngine';

const PRICE_WIDTH = 980;
const PRICE_HEIGHT = 460;
const OSCILLATOR_WIDTH = 980;
const OSCILLATOR_HEIGHT = 190;
const PRICE_PADDING = { top: 18, right: 76, bottom: 58, left: 14 };
const OSCILLATOR_PADDING = { top: 12, right: 76, bottom: 22, left: 14 };
const MIN_OSCILLATOR_HEIGHT = 0;
const MAX_OSCILLATOR_HEIGHT = 280;
const OSCILLATOR_COLLAPSE_THRESHOLD = 34;

// Total height of the oscillator area for a given number of subwindows: one pane keeps the
// classic 160px, additional panes grow the area without letting it swallow the price chart.
function getDefaultOscillatorAreaHeight(count) {
  if (!count) {
    return 0;
  }
  return count === 1 ? 160 : Math.min(340, 110 * count);
}
const MIN_PRICE_SCALE = 0.55;
const MAX_PRICE_SCALE = 3.4;
const PRICE_AXIS_GUTTER = 96;
const PRICE_SCALE_DRAG_THRESHOLD = 8;
const CLICK_DRAG_THRESHOLD = 6;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function isValidValue(value) {
  return value !== null && !Number.isNaN(value);
}

function formatDateTime(timestamp) {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAxisDate(timestamp, includeTime = false) {
  return new Date(timestamp).toLocaleString(undefined, includeTime
    ? { hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: 'numeric' });
}

function getAxisDayKey(timestamp) {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function formatIndicatorNumber(value) {
  const absolute = Math.abs(Number(value) || 0);
  if (absolute >= 100) {
    return Number(value).toFixed(2);
  }
  if (absolute >= 1) {
    return Number(value).toFixed(3);
  }
  if (absolute >= 0.01) {
    return Number(value).toFixed(4);
  }
  return Number(value).toFixed(5);
}

function linePath(values, start, end, width, height, min, max, padding) {
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  let path = '';
  let drawing = false;

  for (let index = start; index < end; index += 1) {
    const value = values[index];
    if (!isValidValue(value)) {
      drawing = false;
      continue;
    }
    const x = padding.left + (((index - start) / Math.max(1, end - start - 1)) * usableWidth);
    const y = padding.top + (((max - value) / Math.max(0.0000001, max - min)) * usableHeight);
    path += `${drawing ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)} `;
    drawing = true;
  }

  return path.trim();
}

function buildAxisLevels(min, max, count = 5) {
  if (!isFinite(min) || !isFinite(max)) {
    return [0];
  }
  if (Math.abs(max - min) < 0.0000001) {
    return [max];
  }
  return Array.from({ length: count }, (_, index) => max - (((max - min) / Math.max(1, count - 1)) * index));
}

function buildTimeAxisTicks(candles, step, crosshairX = null) {
  if (!Array.isArray(candles) || !candles.length) {
    return [];
  }
  if (candles.length === 1) {
    return [{ candle: candles[0], index: 0 }];
  }

  const usableWidth = PRICE_WIDTH - PRICE_PADDING.left - PRICE_PADDING.right;
  const minimumGap = 94;
  const maximumTicks = Math.max(2, Math.min(7, Math.floor(usableWidth / minimumGap)));
  const lastIndex = candles.length - 1;
  const candidates = new Set([0, lastIndex]);
  for (let tick = 1; tick < maximumTicks - 1; tick += 1) {
    candidates.add(Math.round((lastIndex * tick) / Math.max(1, maximumTicks - 1)));
  }

  const reservedCrosshairGap = crosshairX === null ? 0 : 84;
  const accepted = [];
  [...candidates]
    .sort((a, b) => b - a)
    .forEach((index) => {
      const x = PRICE_PADDING.left + (index * step);
      if (reservedCrosshairGap && Math.abs(x - crosshairX) < reservedCrosshairGap) {
        return;
      }
      const overlapsExisting = accepted.some((acceptedIndex) => Math.abs((acceptedIndex - index) * step) < minimumGap);
      if (!overlapsExisting) {
        accepted.push(index);
      }
    });

  const sortedAccepted = accepted
    .sort((a, b) => a - b)
    .map((index) => ({ candle: candles[index], index }));
  const uniqueDays = new Set(candles.map((candle) => getAxisDayKey(candle.time)).filter(Boolean)).size;
  const includeTime = uniqueDays <= 2;
  const seenLabels = new Set();

  return sortedAccepted.reduce((ticks, tick) => {
    const label = formatAxisDate(tick.candle.time, includeTime);
    if (seenLabels.has(label) && ticks.length < sortedAccepted.length - 1) {
      return ticks;
    }
    seenLabels.add(label);
    ticks.push({ ...tick, label });
    return ticks;
  }, []);
}

function getHistogramBars(values, start, end, width, height, min, max, padding, baseValue = 0) {
  const usableWidth = width - padding.left - padding.right;
  const step = usableWidth / Math.max(1, end - start - 1);
  const zeroY = padding.top + (((max - baseValue) / Math.max(0.0000001, max - min)) * (height - padding.top - padding.bottom));
  return Array.from({ length: Math.max(0, end - start) }, (_, offset) => {
    const index = start + offset;
    const value = values[index];
    if (!isValidValue(value)) {
      return null;
    }
    const x = padding.left + (offset * step);
    const y = padding.top + (((max - value) / Math.max(0.0000001, max - min)) * (height - padding.top - padding.bottom));
    return {
      x: x - Math.max(1.5, step * 0.28),
      y: Math.min(y, zeroY),
      width: Math.max(3, step * 0.56),
      height: Math.max(1.5, Math.abs(y - zeroY)),
      positive: value >= baseValue,
    };
  }).filter(Boolean);
}

function getMarkerPoints(shape, x, y, size) {
  switch (shape) {
    case 'triangle-up':
      return `${x},${y - size} ${x - size},${y + size} ${x + size},${y + size}`;
    case 'triangle-down':
      return `${x},${y + size} ${x - size},${y - size} ${x + size},${y - size}`;
    default:
      return '';
  }
}

function getIndexFromClientX(clientX, rect, start, end, width, padding) {
  if (!rect || end <= start) {
    return start;
  }
  const svgX = ((clientX - rect.left) / Math.max(rect.width, 1)) * width;
  const usableWidth = width - padding.left - padding.right;
  const ratio = clamp((svgX - padding.left) / Math.max(usableWidth, 1), 0, 1);
  const relativeIndex = Math.round(ratio * Math.max(0, end - start - 1));
  return clamp(start + relativeIndex, start, end - 1);
}

function getViewBoxY(clientY, rect, height) {
  if (!rect) {
    return null;
  }
  return clamp(((clientY - rect.top) / Math.max(rect.height, 1)) * height, 0, height);
}

function getValueFromY(svgY, min, max, height, padding) {
  if (svgY === null) {
    return null;
  }
  const usableHeight = height - padding.top - padding.bottom;
  const ratio = clamp((svgY - padding.top) / Math.max(usableHeight, 1), 0, 1);
  return max - (ratio * (max - min));
}

function CandleChart({
  chartType,
  engine,
  start,
  end,
  min,
  max,
  yForPrice,
  xForIndex,
  visibleTrades,
  visibleCancelledOrders,
  visiblePendingOrders,
  currentBar,
  playheadX,
  crosshairIndex,
  priceCrossY,
  showCrosshair,
  focusCandle,
  crosshairPrice,
  currentPrice,
  currentPriceY,
  showTradeVisuals,
  patternDrag = null,
  patternWindows = null,
  previewMarks = null,
  previewSelection = null,
  inspectedIndex = null,
  liveFromIndex = null,
  liveFromTime = null,
}) {
  const candles = engine.candles.slice(start, Math.min(end, engine.candles.length));
  const overlays = Array.isArray(engine.overlays)
    ? engine.overlays.map((overlay) => ({
      ...overlay,
      series: Array.isArray(overlay?.series) ? overlay.series : [],
    }))
    : [];
  const usableWidth = PRICE_WIDTH - PRICE_PADDING.left - PRICE_PADDING.right;
  const candleSpace = Math.max(1, end - start - 1);
  const step = usableWidth / candleSpace;
  const candleWidth = Math.max(4, step * 0.56);
  const priceLevels = buildAxisLevels(min, max, 5);
  const crosshairX = showCrosshair ? xForIndex(crosshairIndex) : null;
  const timeAxisTicks = buildTimeAxisTicks(candles, step, crosshairX);

  return (
    <svg viewBox={`0 0 ${PRICE_WIDTH} ${PRICE_HEIGHT}`} preserveAspectRatio="none" className="tester-svg tester-svg-price">
      <rect x="0" y="0" width={PRICE_WIDTH} height={PRICE_HEIGHT} className="tester-svg-bg" />
      {patternDrag ? (() => {
        const lo = clamp(Math.min(patternDrag.lo, patternDrag.hi), start, end - 1);
        const hi = clamp(Math.max(patternDrag.lo, patternDrag.hi), start, end - 1);
        const x1 = xForIndex(lo);
        const x2 = xForIndex(hi);
        const left = Math.min(x1, x2) - (candleWidth / 2);
        const rectWidth = Math.abs(x2 - x1) + candleWidth;
        return <rect x={left} y={PRICE_PADDING.top} width={rectWidth} height={PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom} className="tester-pattern-selection" />;
      })() : null}
      {Array.isArray(patternWindows) ? patternWindows.map((win, index) => {
        const lo = clamp(win.lo, start, end - 1);
        const hi = clamp(win.hi, start, end - 1);
        const left = xForIndex(lo) - (candleWidth / 2);
        const right = xForIndex(hi) + (candleWidth / 2);
        const top = PRICE_PADDING.top;
        const bandHeight = PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom;
        const markerX = xForIndex(hi);
        return (
          <g key={`patwin_${win.side}_${win.hi}_${index}`}>
            <rect
              x={left}
              y={top}
              width={Math.max(2, right - left)}
              height={bandHeight}
              className={win.side === 'short' ? 'tester-pattern-window-short' : 'tester-pattern-window-long'}
            />
            <polygon
              points={`${markerX - 5},${top + 1} ${markerX + 5},${top + 1} ${markerX},${top + 10}`}
              className={win.side === 'short' ? 'tester-pattern-match-short' : 'tester-pattern-match-long'}
            />
          </g>
        );
      }) : null}
      {previewSelection ? (() => {
        const selLo = clamp(previewSelection.lo, start, end - 1);
        const selHi = clamp(previewSelection.hi, start, end - 1);
        if (previewSelection.hi < start || previewSelection.lo > end - 1) return null;
        const left = xForIndex(selLo) - (candleWidth / 2);
        const rectWidth = Math.max(2, xForIndex(selHi) - xForIndex(selLo) + candleWidth);
        return (
          <rect
            x={left}
            y={PRICE_PADDING.top}
            width={rectWidth}
            height={PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom}
            className="tester-pattern-selection"
          />
        );
      })() : null}
      {Number.isFinite(Number(inspectedIndex)) && inspectedIndex !== null && inspectedIndex >= start && inspectedIndex < end ? (() => {
        const markerX = xForIndex(inspectedIndex);
        return (
          <g>
            <rect
              x={markerX - (candleWidth / 2) - 2}
              y={PRICE_PADDING.top}
              width={candleWidth + 4}
              height={PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom}
              className="tester-inspect-mark"
            />
            <circle cx={markerX} cy={PRICE_PADDING.top + 8} r="5" className="tester-inspect-mark-dot" />
          </g>
        );
      })() : null}
      {Array.isArray(previewMarks) ? previewMarks.map((mark) => {
        const markerX = xForIndex(mark.index);
        const left = markerX - (candleWidth / 2);
        const top = PRICE_PADDING.top;
        const bandHeight = PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom;
        return (
          <g key={`condprev_${mark.side}_${mark.index}`}>
            <rect
              x={left}
              y={top}
              width={Math.max(2, candleWidth)}
              height={bandHeight}
              className={mark.side === 'short' ? 'tester-preview-window-short' : 'tester-preview-window-long'}
            />
            <polygon
              points={`${markerX - 5},${top + 1} ${markerX + 5},${top + 1} ${markerX},${top + 10}`}
              className={mark.side === 'short' ? 'tester-pattern-match-short' : 'tester-pattern-match-long'}
            />
          </g>
        );
      }) : null}
      {priceLevels.map((value, index) => {
        const y = yForPrice(value);
        return (
          <g key={index}>
            <line x1={PRICE_PADDING.left} x2={PRICE_WIDTH - PRICE_PADDING.right} y1={y} y2={y} className="tester-grid-line" />
            <text x={PRICE_WIDTH - PRICE_PADDING.right + 10} y={y + 4} className="tester-axis-text">{formatPrice(value, engine.profile)}</text>
          </g>
        );
      })}

      {chartType === 'Line' && (
        <path
          d={linePath(engine.candles.map((candle) => candle.close), start, end, PRICE_WIDTH, PRICE_HEIGHT, min, max, PRICE_PADDING)}
          className="tester-price-line"
        />
      )}

      {chartType === 'Bars' && candles.map((candle, index) => {
        const x = PRICE_PADDING.left + (index * step);
        const bullish = candle.close >= candle.open;
        return (
          <g key={candle.index} className={bullish ? 'tester-bull' : 'tester-bear'}>
            <line x1={x} x2={x} y1={yForPrice(candle.high)} y2={yForPrice(candle.low)} className="tester-candle-stem" />
            <line x1={x - 6} x2={x} y1={yForPrice(candle.open)} y2={yForPrice(candle.open)} className="tester-candle-stem" />
            <line x1={x} x2={x + 6} y1={yForPrice(candle.close)} y2={yForPrice(candle.close)} className="tester-candle-stem" />
          </g>
        );
      })}

      {chartType === 'Candles' && candles.map((candle, index) => {
        const x = PRICE_PADDING.left + (index * step);
        const bullish = candle.close >= candle.open;
        const top = yForPrice(Math.max(candle.open, candle.close));
        const bottom = yForPrice(Math.min(candle.open, candle.close));
        return (
          <g key={candle.index} className={bullish ? 'tester-bull' : 'tester-bear'}>
            <line x1={x} x2={x} y1={yForPrice(candle.high)} y2={yForPrice(candle.low)} className="tester-candle-stem" />
            <rect x={x - (candleWidth / 2)} y={top} width={candleWidth} height={Math.max(2, bottom - top)} className="tester-candle-body" />
          </g>
        );
      })}

      {overlays.flatMap((overlay) => overlay.series).map((series) => {
        if (series.type === 'histogram') {
          return getHistogramBars(series.values, start, end, PRICE_WIDTH, PRICE_HEIGHT, min, max, PRICE_PADDING, series.baseValue ?? 0)
            .map((bar, index) => (
              <rect
                key={`${series.id}_${index}`}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.positive ? series.positiveColor : series.negativeColor}
                opacity="0.85"
              />
            ));
        }
        if (series.type === 'markers') {
          return series.values.slice(start, end).map((value, offset) => {
            if (!isValidValue(value)) {
              return null;
            }
            const x = PRICE_PADDING.left + (offset * step);
            const y = yForPrice(value);
            if (series.shape === 'circle') {
              return <circle key={`${series.id}_${offset}`} cx={x} cy={y} r="3.2" fill={series.color} opacity="0.95" />;
            }
            return (
              <polygon
                key={`${series.id}_${offset}`}
                points={getMarkerPoints(series.shape, x, y, 4.6)}
                fill={series.color}
                opacity="0.95"
              />
            );
          }).filter(Boolean);
        }
        return (
          <path
            key={series.id}
            d={linePath(series.values, start, end, PRICE_WIDTH, PRICE_HEIGHT, min, max, PRICE_PADDING)}
            fill="none"
            stroke={series.color}
            strokeWidth={series.strokeWidth || 2}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray={series.dashArray || undefined}
          />
        );
      })}

      {/* Deployment boundary. Everything to the right of this line was traded FORWARD, on
          bars the strategy had never seen when it was deployed — which is the difference
          between a backtest and a real track record, and worth showing rather than stating. */}
      {Number.isFinite(liveFromIndex) && liveFromIndex > start && liveFromIndex < end ? (() => {
        const boundaryX = xForIndex(liveFromIndex);
        // Label with the DEPLOYMENT date, not the bar's. The line necessarily sits on the
        // first bar at or after deployment, which over a weekend can be two days later —
        // and "when did this go live" is the question being answered.
        const boundaryCandle = engine.candles[liveFromIndex];
        const labelTime = Number(liveFromTime) > 0 ? Number(liveFromTime) : boundaryCandle?.time;
        const label = labelTime
          ? new Date(labelTime).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })
          : '';
        return (
          <g className="tester-deploy-boundary">
            <rect
              x={boundaryX}
              y={PRICE_PADDING.top}
              width={Math.max(0, PRICE_WIDTH - PRICE_PADDING.right - boundaryX)}
              height={PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom}
              className="tester-deploy-region"
            />
            <line
              x1={boundaryX}
              x2={boundaryX}
              y1={PRICE_PADDING.top}
              y2={PRICE_HEIGHT - PRICE_PADDING.bottom}
              className="tester-deploy-line"
            />
            <text x={boundaryX + 6} y={PRICE_PADDING.top + 13} className="tester-deploy-label">
              {`Deployed ${label}`}
            </text>
            <text x={boundaryX + 6} y={PRICE_PADDING.top + 26} className="tester-deploy-sublabel">
              forward test →
            </text>
          </g>
        );
      })() : null}

      {showTradeVisuals && visibleTrades.map((trade) => {
          const entryX = xForIndex(trade.entryIndex);
          const displayExitIndex = trade.exitIndex <= currentBar
            ? (trade.exitIndex ?? trade.entryIndex)
            : currentBar;
          const exitX = xForIndex(Math.min(displayExitIndex, end - 1));
        const left = Math.min(entryX, exitX);
        const width = Math.max(6, Math.abs(exitX - entryX));
        const entryY = yForPrice(trade.entry);
        const stopY = yForPrice(trade.stopPrice ?? trade.entry);
        const targetY = yForPrice(trade.targetPrice ?? trade.entry);
        const rewardTop = Math.min(entryY, targetY);
        const rewardHeight = Math.max(2, Math.abs(targetY - entryY));
        const riskTop = Math.min(entryY, stopY);
        const riskHeight = Math.max(2, Math.abs(stopY - entryY));
        const hasTarget = Number.isFinite(trade.targetPrice) && trade.targetPrice !== trade.entry;
        const hasStop = Number.isFinite(trade.stopPrice) && trade.stopPrice !== trade.entry;
        // Moving-level path: when the stop/target changed during the trade (trailing,
        // breakeven, script-managed), draw the bar-by-bar stair-step instead of one flat
        // line. Segments are clipped to the replay position so the level visibly walks.
        const clampX = (barIndex) => xForIndex(Math.max(start, Math.min(barIndex, end - 1)));
        const buildLevelPath = (key) => {
          const levels = (Array.isArray(trade.levels) ? trade.levels : []).filter((level) => Number.isFinite(level[key]));
          if (levels.length < 2) return null;
          let path = '';
          let started = false;
          for (let i = 0; i < levels.length; i += 1) {
            const from = Math.max(levels[i].index, trade.entryIndex);
            const to = Math.min(i + 1 < levels.length ? levels[i + 1].index : displayExitIndex, displayExitIndex);
            if (from > displayExitIndex || to < from) continue;
            const y = yForPrice(levels[i][key]);
            const x1 = clampX(from);
            const x2 = clampX(to);
            if (!started) {
              path += `M ${x1} ${y}`;
              started = true;
            } else {
              path += ` L ${x1} ${y}`;
            }
            path += ` L ${x2} ${y}`;
          }
          return started ? path : null;
        };
        const stopPath = buildLevelPath('stop');
        const targetPath = buildLevelPath('target');
        // Moving risk/reward zones: when the level walked, the shaded rectangle follows it
        // segment by segment (entry price to the level active on those bars) instead of
        // painting one static box across the whole trade.
        const buildZoneSegments = (key) => {
          const levels = (Array.isArray(trade.levels) ? trade.levels : []).filter((level) => Number.isFinite(level[key]));
          if (levels.length < 2) return null;
          const segments = [];
          for (let i = 0; i < levels.length; i += 1) {
            const from = Math.max(levels[i].index, trade.entryIndex);
            const to = Math.min(i + 1 < levels.length ? levels[i + 1].index : displayExitIndex, displayExitIndex);
            if (from > displayExitIndex || to < from) continue;
            const x1 = clampX(from);
            const x2 = clampX(to);
            const levelY = yForPrice(levels[i][key]);
            segments.push({
              x: Math.min(x1, x2),
              width: Math.max(2, Math.abs(x2 - x1)),
              y: Math.min(entryY, levelY),
              height: Math.max(2, Math.abs(levelY - entryY)),
            });
          }
          return segments.length ? segments : null;
        };
        const stopZones = buildZoneSegments('stop');
        const targetZones = buildZoneSegments('target');
        const markerY = trade.side === 'BUY'
          ? yForPrice(engine.candles[trade.entryIndex].low) + 16
          : yForPrice(engine.candles[trade.entryIndex].high) - 16;
        const points = trade.side === 'BUY'
          ? `${entryX},${markerY} ${entryX - 6},${markerY + 10} ${entryX + 6},${markerY + 10}`
          : `${entryX},${markerY} ${entryX - 6},${markerY - 10} ${entryX + 6},${markerY - 10}`;
        // Trades before the deployment bar are replayed history; after it they were taken
        // forward on unseen bars. Same geometry, deliberately different weight.
        const isForward = Number.isFinite(liveFromIndex) && trade.entryIndex >= liveFromIndex;
        return (
          <g
            key={trade.id}
            className={Number.isFinite(liveFromIndex)
              ? (isForward ? 'tester-trade-forward' : 'tester-trade-backtest')
              : undefined}
          >
            {targetZones
              ? targetZones.map((zone, zoneIndex) => (
                <rect key={`tzone_${zoneIndex}`} x={zone.x} y={zone.y} width={zone.width} height={zone.height} className="tester-trade-zone tester-trade-zone-profit" />
              ))
              : <rect x={left} y={rewardTop} width={width} height={rewardHeight} className="tester-trade-zone tester-trade-zone-profit" />}
            {stopZones
              ? stopZones.map((zone, zoneIndex) => (
                <rect key={`szone_${zoneIndex}`} x={zone.x} y={zone.y} width={zone.width} height={zone.height} className="tester-trade-zone tester-trade-zone-risk" />
              ))
              : <rect x={left} y={riskTop} width={width} height={riskHeight} className="tester-trade-zone tester-trade-zone-risk" />}
            {targetPath
              ? <path d={targetPath} fill="none" className="tester-trade-target-line" />
              : hasTarget ? <line x1={left} x2={left + width} y1={targetY} y2={targetY} className="tester-trade-target-line" /> : null}
            {stopPath
              ? <path d={stopPath} fill="none" className="tester-trade-stop-line" />
              : hasStop ? <line x1={left} x2={left + width} y1={stopY} y2={stopY} className="tester-trade-stop-line" /> : null}
            <line x1={left} x2={left + width} y1={entryY} y2={entryY} className="tester-trade-entry-line" />
            <polygon points={points} className={trade.side === 'BUY' ? 'tester-marker-buy' : 'tester-marker-sell'} />
          </g>
        );
      })}

      {showTradeVisuals && visibleCancelledOrders.map((order, idx) => {
        const cx = xForIndex(order.placedIndex);
        const cy = yForPrice(order.price);
        return (
          <g key={`cancelled_${idx}`} opacity="0.55">
            <circle cx={cx} cy={cy} r="5" className={order.side === 'long' ? 'tester-pending-buy' : 'tester-pending-sell'} />
            <line x1={cx - 4} y1={cy - 4} x2={cx + 4} y2={cy + 4} className="tester-pending-cancel" />
            <line x1={cx + 4} y1={cy - 4} x2={cx - 4} y2={cy + 4} className="tester-pending-cancel" />
          </g>
        );
      })}

      {showTradeVisuals && (visiblePendingOrders || []).map((order, idx) => {
        const x1 = xForIndex(Math.max(order.placedIndex, start));
        const x2 = xForIndex(Math.min(currentBar, end - 1));
        const cy = yForPrice(order.price);
        const isBuy = order.side === 'long';
        const markerX = x2;
        const points = isBuy
          ? `${markerX},${cy} ${markerX + 9},${cy - 5} ${markerX + 9},${cy + 5}`
          : `${markerX},${cy} ${markerX + 9},${cy - 5} ${markerX + 9},${cy + 5}`;
        return (
          <g key={`pending_${order.id || idx}`} opacity="0.8">
            <line x1={x1} y1={cy} x2={x2} y2={cy} className={isBuy ? 'tester-pending-line-buy' : 'tester-pending-line-sell'} strokeDasharray="4 3" />
            <polygon points={points} className={isBuy ? 'tester-pending-buy' : 'tester-pending-sell'} />
            <text x={x1 + 3} y={cy - 4} className="tester-pending-label">{`${order.type === 'limit' ? 'LIMIT' : 'STOP'} ${isBuy ? 'BUY' : 'SELL'}`}</text>
          </g>
        );
      })}

      <line x1={PRICE_PADDING.left} x2={PRICE_WIDTH - PRICE_PADDING.right} y1={currentPriceY} y2={currentPriceY} className="tester-live-price-line" />
      <g className="tester-live-price-tag">
        <rect x={PRICE_WIDTH - PRICE_PADDING.right + 8} y={Math.max(PRICE_PADDING.top, Math.min(PRICE_HEIGHT - PRICE_PADDING.bottom - 18, currentPriceY - 10))} width="62" height="20" rx="10" />
        <text x={PRICE_WIDTH - PRICE_PADDING.right + 39} y={Math.max(PRICE_PADDING.top + 13, Math.min(PRICE_HEIGHT - PRICE_PADDING.bottom + 3, currentPriceY + 4))} className="tester-axis-tag-text">
          {formatPrice(currentPrice, engine.profile)}
        </text>
      </g>

      <line x1={playheadX} x2={playheadX} y1={PRICE_PADDING.top} y2={PRICE_HEIGHT - PRICE_PADDING.bottom} className="tester-playhead" />

      {showCrosshair && crosshairX !== null && (
        <>
          <line x1={crosshairX} x2={crosshairX} y1={PRICE_PADDING.top} y2={PRICE_HEIGHT - PRICE_PADDING.bottom} className="tester-crosshair-line" />
          {priceCrossY !== null && (
            <line x1={PRICE_PADDING.left} x2={PRICE_WIDTH - PRICE_PADDING.right} y1={priceCrossY} y2={priceCrossY} className="tester-crosshair-line" />
          )}
          <circle cx={crosshairX} cy={yForPrice(focusCandle.close)} r="4.6" className="tester-crosshair-point" />
          {crosshairPrice !== null && (
            <g className="tester-axis-tag">
              <rect x={PRICE_WIDTH - PRICE_PADDING.right + 8} y={Math.max(PRICE_PADDING.top, Math.min(PRICE_HEIGHT - PRICE_PADDING.bottom - 18, (priceCrossY ?? yForPrice(focusCandle.close)) - 10))} width="62" height="20" rx="10" />
              <text x={PRICE_WIDTH - PRICE_PADDING.right + 39} y={Math.max(PRICE_PADDING.top + 13, Math.min(PRICE_HEIGHT - PRICE_PADDING.bottom + 3, (priceCrossY ?? yForPrice(focusCandle.close)) + 4))} className="tester-axis-tag-text">
                {formatPrice(crosshairPrice, engine.profile)}
              </text>
            </g>
          )}
          <g className="tester-axis-tag tester-time-axis-tag">
            <rect
              x={Math.max(PRICE_PADDING.left, Math.min(PRICE_WIDTH - PRICE_PADDING.right - 126, crosshairX - 63))}
              y={PRICE_HEIGHT - PRICE_PADDING.bottom + 10}
              width="126"
              height="20"
              rx="10"
            />
            <text
              x={Math.max(PRICE_PADDING.left + 63, Math.min(PRICE_WIDTH - PRICE_PADDING.right - 63, crosshairX))}
              y={PRICE_HEIGHT - PRICE_PADDING.bottom + 24}
              className="tester-axis-tag-text"
            >
              {formatDateTime(focusCandle.time)}
            </text>
          </g>
        </>
      )}

      {timeAxisTicks.map(({ candle, index, label }) => {
          const x = PRICE_PADDING.left + (index * step);
          const anchor = index === 0 ? 'start' : index === candles.length - 1 ? 'end' : 'middle';
          return (
            <text key={candle.index} x={x} y={PRICE_HEIGHT - 10} className="tester-axis-text tester-axis-date" textAnchor={anchor}>
              {label}
            </text>
          );
        })}
    </svg>
  );
}

function OscillatorChart({
  oscillator,
  start,
  end,
  min,
  max,
  yForOscillator,
  xForIndex,
  crosshairIndex,
  showCrosshair,
  focusOscillatorReadings,
}) {
  const guides = oscillator.guides || [];
  const crosshairX = showCrosshair ? xForIndex(crosshairIndex) : null;
  const oscillatorLevels = buildAxisLevels(min, max, 5);
  const primaryReading = focusOscillatorReadings.find((series) => isValidValue(series.value));
  const oscillatorValueY = primaryReading && isValidValue(primaryReading.value) ? yForOscillator(primaryReading.value) : null;
  const zeroGuide = guides.includes(0) ? null : (min < 0 && max > 0 ? 0 : null);

  return (
    <svg viewBox={`0 0 ${OSCILLATOR_WIDTH} ${OSCILLATOR_HEIGHT}`} preserveAspectRatio="none" className="tester-svg tester-svg-oscillator">
      <rect x="0" y="0" width={OSCILLATOR_WIDTH} height={OSCILLATOR_HEIGHT} className="tester-svg-panel" />
      {oscillatorLevels.map((value) => {
        const y = yForOscillator(value);
        return (
          <g key={`axis_${value}`}>
            <line x1={OSCILLATOR_PADDING.left} x2={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right} y1={y} y2={y} className="tester-grid-line" />
            <text x={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right + 10} y={y + 4} className="tester-axis-text">{formatIndicatorNumber(value)}</text>
          </g>
        );
      })}
      {guides.map((guide) => <line key={guide} x1={OSCILLATOR_PADDING.left} x2={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right} y1={yForOscillator(guide)} y2={yForOscillator(guide)} className="tester-guide-line" />)}
      {zeroGuide !== null && <line x1={OSCILLATOR_PADDING.left} x2={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right} y1={yForOscillator(zeroGuide)} y2={yForOscillator(zeroGuide)} className="tester-guide-line tester-guide-line-zero" />}
      {oscillator.series.map((series) => {
        if (series.type === 'histogram') {
          return getHistogramBars(series.values, start, end, OSCILLATOR_WIDTH, OSCILLATOR_HEIGHT, min, max, OSCILLATOR_PADDING, series.baseValue ?? 0)
            .map((bar, index) => (
              <rect
                key={`${series.id}_${index}`}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.positive ? series.positiveColor : series.negativeColor}
                opacity="0.92"
              />
            ));
        }
        if (series.type === 'markers') {
          return series.values.slice(start, end).map((value, offset) => {
            if (!isValidValue(value)) {
              return null;
            }
            const x = OSCILLATOR_PADDING.left + ((offset / Math.max(1, end - start - 1)) * (OSCILLATOR_WIDTH - OSCILLATOR_PADDING.left - OSCILLATOR_PADDING.right));
            const y = yForOscillator(value);
            if (series.shape === 'circle') {
              return <circle key={`${series.id}_${offset}`} cx={x} cy={y} r="3.2" fill={series.color} opacity="0.95" />;
            }
            return <polygon key={`${series.id}_${offset}`} points={getMarkerPoints(series.shape, x, y, 4.2)} fill={series.color} opacity="0.95" />;
          }).filter(Boolean);
        }
        return (
          <path
            key={series.id}
            d={linePath(series.values, start, end, OSCILLATOR_WIDTH, OSCILLATOR_HEIGHT, min, max, OSCILLATOR_PADDING)}
            fill="none"
            stroke={series.color}
            strokeWidth={series.strokeWidth || 2}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray={series.dashArray || undefined}
          />
        );
      })}
        <text x={OSCILLATOR_PADDING.left} y={16} className="tester-axis-text tester-legend-text">{oscillator.label}</text>
      {showCrosshair && crosshairX !== null && (
        <>
          <line x1={crosshairX} x2={crosshairX} y1={OSCILLATOR_PADDING.top} y2={OSCILLATOR_HEIGHT - OSCILLATOR_PADDING.bottom} className="tester-crosshair-line" />
          {oscillatorValueY !== null && (
            <line x1={OSCILLATOR_PADDING.left} x2={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right} y1={oscillatorValueY} y2={oscillatorValueY} className="tester-crosshair-line" />
          )}
          {oscillatorValueY !== null && <circle cx={crosshairX} cy={oscillatorValueY} r="4.2" className="tester-crosshair-point tester-crosshair-point-oscillator" />}
          {primaryReading && isValidValue(primaryReading.value) && (
            <g className="tester-axis-tag">
              <rect x={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right + 8} y={Math.max(OSCILLATOR_PADDING.top, Math.min(OSCILLATOR_HEIGHT - OSCILLATOR_PADDING.bottom - 18, oscillatorValueY - 10))} width="62" height="20" rx="10" />
              <text x={OSCILLATOR_WIDTH - OSCILLATOR_PADDING.right + 39} y={Math.max(OSCILLATOR_PADDING.top + 13, Math.min(OSCILLATOR_HEIGHT - OSCILLATOR_PADDING.bottom + 3, oscillatorValueY + 4))} className="tester-axis-tag-text">
                {formatIndicatorNumber(primaryReading.value)}
              </text>
            </g>
          )}
        </>
      )}
    </svg>
  );
}

export function EquityMiniChart({ summary, detailed = false, onExpand = null }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const points = Array.isArray(summary?.equity) ? summary.equity : [];
  const width = detailed ? 920 : 320;
  const height = detailed ? 380 : 120;
  const padding = detailed
    ? { top: 24, right: 84, bottom: 52, left: 78 }
    : { top: 10, right: 10, bottom: 10, left: 10 };
  const shellClassName = `tester-equity-shell ${detailed ? 'is-expanded' : ''} ${onExpand ? 'is-interactive' : ''}`.trim();
  const shellProps = onExpand
    ? {
      role: 'button',
      tabIndex: 0,
      onClick: onExpand,
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onExpand();
        }
      },
      title: 'Click to expand equity curve',
    }
    : {};
  if (!points.length) {
    return (
      <div className={shellClassName} {...shellProps}>
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className={`tester-svg tester-svg-equity ${detailed ? 'tester-svg-equity-detailed' : ''}`}>
          <rect x="0" y="0" width={width} height={height} className="tester-svg-panel" />
        </svg>
      </div>
    );
  }

  const values = points.flatMap((point) => [point.balance, point.equity].filter((value) => Number.isFinite(value)));
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const axisPadding = detailed ? Math.max((rawMax - rawMin) * 0.06, 1) : 0;
  const min = rawMin - axisPadding;
  const max = rawMax + axisPadding;
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  const getX = (index) => padding.left + ((index / Math.max(1, points.length - 1)) * usableWidth);
  const getY = (value) => padding.top + (((max - value) / Math.max(0.0000001, max - min)) * usableHeight);
  const buildPath = (key) => points.map((point, index) => {
    const x = getX(index);
    const y = getY(point[key]);
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');
  const equityPath = buildPath('equity');
  const balancePath = buildPath('balance');
  const area = `${equityPath} L${width - padding.right} ${height - padding.bottom} L${padding.left} ${height - padding.bottom} Z`;
  const latestPoint = points[points.length - 1];
  const yLevels = detailed ? buildAxisLevels(min, max, 5) : [];
  const xLevelIndexes = detailed
    ? Array.from(new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])).filter((index) => index >= 0 && index < points.length)
    : [];
  const activeIndex = detailed && hoverIndex !== null ? hoverIndex : null;
  const activePoint = activeIndex !== null ? points[activeIndex] : null;
  const activeX = activeIndex !== null ? getX(activeIndex) : null;
  const hoverCardX = activeX === null
    ? 0
    : Math.max(padding.left + 8, Math.min(width - padding.right - 176, activeX + 12));
  const formatMoneyAxis = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const formatMoneyValue = (value) => Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function handlePointerMove(event) {
    if (!detailed) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const svgX = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * width;
    const plotX = clamp(svgX, padding.left, width - padding.right);
    const ratio = (plotX - padding.left) / Math.max(usableWidth, 1);
    setHoverIndex(clamp(Math.round(ratio * (points.length - 1)), 0, points.length - 1));
  }

  return (
    <div className={shellClassName} {...shellProps}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className={`tester-svg tester-svg-equity ${detailed ? 'tester-svg-equity-detailed' : ''}`}
        onPointerMove={handlePointerMove}
        onPointerLeave={() => setHoverIndex(null)}
        role="img"
        aria-label="Equity and balance curve"
      >
        <rect x="0" y="0" width={width} height={height} className="tester-svg-panel" />
        {detailed && yLevels.map((value) => {
          const y = getY(value);
          return (
            <g key={`y-${value}`}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} className="tester-equity-gridline" />
              <text x={padding.left - 10} y={y + 4} className="tester-equity-axis-text" textAnchor="end">
                {formatMoneyAxis(value)}
              </text>
            </g>
          );
        })}
        {detailed && xLevelIndexes.map((index) => {
          const point = points[index];
          const x = getX(index);
          return (
            <g key={`x-${index}`}>
              <line x1={x} x2={x} y1={padding.top} y2={height - padding.bottom} className="tester-equity-gridline muted" />
              <text x={x} y={height - padding.bottom + 28} className="tester-equity-axis-text" textAnchor="middle">
                {formatDateTime(point.time)}
              </text>
            </g>
          );
        })}
        {detailed && (
          <>
            <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="tester-equity-axis" />
            <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="tester-equity-axis" />
            <text x={padding.left} y={14} className="tester-equity-axis-title">Account value</text>
            <text x={(padding.left + width - padding.right) / 2} y={height - 10} className="tester-equity-axis-title" textAnchor="middle">Time</text>
          </>
        )}
        <path d={area} className="tester-equity-area" />
        <path d={balancePath} className="tester-balance-line" />
        <path d={equityPath} className="tester-equity-line" />
        <circle
          cx={width - padding.right}
          cy={getY(latestPoint.balance)}
          r="2.8"
          className="tester-balance-dot"
        />
        <circle
          cx={width - padding.right}
          cy={getY(latestPoint.equity)}
          r="3.2"
          className="tester-equity-dot"
        />
        {activePoint && activeX !== null && (
          <g className="tester-equity-hover-card">
            <line x1={activeX} x2={activeX} y1={padding.top} y2={height - padding.bottom} className="tester-equity-hover-line" />
            <circle cx={activeX} cy={getY(activePoint.balance)} r="4" className="tester-balance-dot" />
            <circle cx={activeX} cy={getY(activePoint.equity)} r="4.4" className="tester-equity-dot" />
            <rect x={hoverCardX} y={padding.top + 10} width="166" height="70" rx="12" />
            <text x={hoverCardX + 12} y={padding.top + 30}>{formatDateTime(activePoint.time)}</text>
            <text x={hoverCardX + 12} y={padding.top + 50}>Equity {formatMoneyValue(activePoint.equity)}</text>
            <text x={hoverCardX + 12} y={padding.top + 68}>Balance {formatMoneyValue(activePoint.balance)}</text>
          </g>
        )}
      </svg>
      <div className="tester-equity-legend">
        <span><i className="tester-equity-legend-chip tester-equity-legend-chip-balance" />Balance</span>
        <span><i className="tester-equity-legend-chip tester-equity-legend-chip-equity" />Equity</span>
        {!detailed && onExpand && <span>Click to pop out</span>}
      </div>
    </div>
  );
}

export default function StrategyTesterChart({ engine, currentBar, chartType, viewResetRevision = 0, onRequestOlder = null, canRequestOlder = false, isRequestingOlder = false, onChartFocus = null, selectMode = false, onRangeSelected = null, liveRules = null, conditionPreview = null, onBarInspect = null, inspectedIndex = null, liveFromTime = null }) {
  const chartRootRef = useRef(null);
  const pricePaneRef = useRef(null);
  const oscillatorPaneRef = useRef(null);
  const splitterRef = useRef(null);
  const panStateRef = useRef(null);
  const markerDragStateRef = useRef(null);
  const olderRequestKeyRef = useRef(null);
  const lastResetRevisionRef = useRef(-1);
  const priceScaleStateRef = useRef(null);
  const resizeStateRef = useRef(null);
  const touchPointsRef = useRef(new Map());
  const touchGestureRef = useRef(null);
  const [visibleBars, setVisibleBars] = useState(92);
  const [panOffset, setPanOffset] = useState(0);
  const [priceScale, setPriceScale] = useState(1);
  const [oscillatorHeight, setOscillatorHeight] = useState(() => getDefaultOscillatorAreaHeight(
    Array.isArray(engine.oscillators) ? engine.oscillators.length : (engine.oscillator ? 1 : 0),
  ));
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [priceCrossY, setPriceCrossY] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [isScalingPrice, setIsScalingPrice] = useState(false);
  const [isResizingOscillator, setIsResizingOscillator] = useState(false);
  const [readoutCollapsed, setReadoutCollapsed] = useState(false);
  const [showTradeVisuals, setShowTradeVisuals] = useState(true);
  const [followLive, setFollowLive] = useState(true);
  const [markerIndex, setMarkerIndex] = useState(currentBar);
  const [patternDrag, setPatternDrag] = useState(null); // { lo, hi } bar indices during selection
  const patternDragActiveRef = useRef(false);
  const [inspectMode, setInspectMode] = useState(false); // "why did/didn't it trade here?" click mode

  const safeOverlays = Array.isArray(engine.overlays)
    ? engine.overlays.map((overlay) => ({
      ...overlay,
      series: Array.isArray(overlay?.series) ? overlay.series : [],
    }))
    : [];
  // Every oscillator gets its own subwindow (MT5-style). Older engine payloads only carry a
  // single `oscillator`, so fall back to that.
  const safeOscillators = (Array.isArray(engine.oscillators)
    ? engine.oscillators
    : (engine.oscillator ? [engine.oscillator] : []))
    .filter(Boolean)
    .map((entry) => ({
      ...entry,
      series: Array.isArray(entry?.series) ? entry.series : [],
      guides: Array.isArray(entry?.guides) ? entry.guides : [],
    }));
  const oscillatorCount = safeOscillators.length;
  const safeTrades = Array.isArray(engine.trades) ? engine.trades : [];
  const safeCancelledOrders = Array.isArray(engine.cancelledOrders) ? engine.cancelledOrders : [];
  const safePendingHistory = Array.isArray(engine.pendingOrderHistory) ? engine.pendingOrderHistory : [];
  const totalCandles = engine.candles.length;
  const minVisibleBars = useMemo(() => Math.min(Math.max(24, engine.startIndex > 40 ? 28 : 20), totalCandles), [engine.startIndex, totalCandles]);
  const maxVisibleBars = useMemo(() => Math.min(180, totalCandles), [totalCandles]);
  const defaultVisibleBars = useMemo(() => clamp(Math.min(92, totalCandles), minVisibleBars, maxVisibleBars), [maxVisibleBars, minVisibleBars, totalCandles]);
  const liveEnd = clamp(Math.max(engine.startIndex + 1, currentBar + 1), 1, totalCandles);

  useEffect(() => {
    if (lastResetRevisionRef.current === viewResetRevision) {
      return;
    }
    lastResetRevisionRef.current = viewResetRevision;
    setVisibleBars(defaultVisibleBars);
    setPanOffset(0);
    setPriceScale(1);
    setOscillatorHeight(getDefaultOscillatorAreaHeight(oscillatorCount));
    setHoveredIndex(null);
    setPriceCrossY(null);
    setMarkerIndex(currentBar);
    setFollowLive(true);
  }, [viewResetRevision, currentBar, defaultVisibleBars, oscillatorCount]);

  // Chart shift (MT5-style): dragging past the live candle goes NEGATIVE, leaving empty
  // space on the right so the latest candle can sit anywhere on screen. Auto-follow keeps
  // the chosen shift as new bars arrive.
  const maxRightShift = Math.max(0, visibleBars - 12);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const nextMaxPanOffset = Math.max(0, liveEnd - Math.min(visibleBars, liveEnd));
    setPanOffset((previous) => clamp(previous, -Math.max(0, visibleBars - 12), nextMaxPanOffset));
  }, [liveEnd, visibleBars]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (followLive) {
      setMarkerIndex(currentBar);
    }
  }, [currentBar, followLive]);

  // Manually scrolling/panning into history turns off auto-follow, so re-running the backtest
  // (e.g. after editing a rule) won't yank the chart forward to the live candle.
  useEffect(() => {
    if (panOffset > 0 && followLive) {
      setFollowLive(false);
    }
  }, [panOffset, followLive]);

  const maxPanOffset = Math.max(0, liveEnd - Math.min(visibleBars, liveEnd));
  const safePanOffset = clamp(panOffset, -maxRightShift, maxPanOffset);
  // `end` is virtual: with a negative offset (chart shift) it extends past the last candle,
  // and the empty slots render as blank space on the right. Data reads stop at lastDataIndex.
  const end = Math.max(Math.min(visibleBars, totalCandles), liveEnd - safePanOffset);
  const start = Math.max(0, end - visibleBars);
  const lastDataIndex = Math.min(end, totalCandles) - 1;
  const visibleCandles = engine.candles.slice(start, Math.min(end, totalCandles));
  const priceValues = [
    ...visibleCandles.flatMap((candle) => [candle.high, candle.low]),
    ...safeOverlays.flatMap((overlay) => overlay.series.flatMap((series) => (Array.isArray(series.values) ? series.values.slice(start, end).filter(isValidValue) : []))),
  ];
  const rawPriceMin = Math.min(...priceValues);
  const rawPriceMax = Math.max(...priceValues);
  const pricePaddingValue = Math.max((rawPriceMax - rawPriceMin) * 0.08, engine.profile.point * 8);
  const priceCenter = (rawPriceMin + rawPriceMax) / 2;
  const basePriceRange = Math.max((rawPriceMax - rawPriceMin) + (pricePaddingValue * 2), engine.profile.point * 80);
  const scaledPriceRange = basePriceRange / priceScale;
  const priceMin = priceCenter - (scaledPriceRange / 2);
  const priceMax = priceCenter + (scaledPriceRange / 2);
  const oscillatorVisible = oscillatorCount > 0;
  const priceUsableWidth = PRICE_WIDTH - PRICE_PADDING.left - PRICE_PADDING.right;
  const priceUsableHeight = PRICE_HEIGHT - PRICE_PADDING.top - PRICE_PADDING.bottom;
  const oscillatorUsableHeight = OSCILLATOR_HEIGHT - OSCILLATOR_PADDING.top - OSCILLATOR_PADDING.bottom;
  const xForIndex = (index) => PRICE_PADDING.left + (((index - start) / Math.max(1, end - start - 1)) * priceUsableWidth);
  const yForPrice = (value) => PRICE_PADDING.top + (((priceMax - value) / Math.max(0.0000001, priceMax - priceMin)) * priceUsableHeight);
  // One scale per subwindow: each oscillator keeps its own min/max (RSI 0-100 next to an
  // unbounded MACD must not share an axis).
  const oscillatorPanes = safeOscillators.map((oscillatorEntry) => {
    const visibleValues = oscillatorEntry.series.flatMap((series) => {
      const values = series.type === 'histogram'
        ? [...(Array.isArray(series.values) ? series.values.slice(start, end) : []), series.baseValue ?? 0]
        : (Array.isArray(series.values) ? series.values.slice(start, end) : []);
      return values.filter(isValidValue);
    });
    const min = oscillatorEntry.min ?? (visibleValues.length ? Math.min(...visibleValues) : 0);
    const max = oscillatorEntry.max ?? (visibleValues.length ? Math.max(...visibleValues) : 100);
    const yForOscillator = (value) => OSCILLATOR_PADDING.top + (((max - value) / Math.max(0.0000001, max - min)) * oscillatorUsableHeight);
    return { oscillator: oscillatorEntry, min, max, yForOscillator };
  });
  const visibleTrades = safeTrades.filter((trade) => trade.entryIndex >= start && trade.entryIndex < end && trade.entryIndex <= currentBar);
  // Bar index where live deployment began. Resolved from a TIME rather than an index because
  // the caller only knows when the deployment started, and candle indices shift with the window.
  const liveFromIndex = useMemo(() => {
    const target = Number(liveFromTime);
    if (!Number.isFinite(target) || target <= 0) return null;
    const candles = engine?.candles || [];
    const found = candles.findIndex((candle) => Number(candle.time) >= target);
    return found >= 0 ? found : null;
  }, [engine, liveFromTime]);
  const visibleCancelledOrders = safeCancelledOrders.filter((order) => order.placedIndex >= start && order.placedIndex < end && order.placedIndex <= currentBar);
  // A pending order is "live" at the current replay bar when it has been placed but not yet
  // triggered/cancelled/expired. Draw those resting on the chart so the replay shows orders
  // waiting to fill, then disappearing when they resolve.
  const visiblePendingOrders = safePendingHistory.filter((order) => {
    if (order.placedIndex > currentBar) return false;
    const stillLive = order.resolvedIndex === null || order.resolvedIndex === undefined || currentBar < order.resolvedIndex;
    return stillLive && order.placedIndex < end && currentBar >= start;
  });
  // Live pattern-match overlay: indices where the close window ending there matches the stored
  // template at the current threshold. Uses the same correlation as the engines, so what lights
  // up here is exactly what the backtest/EA will trade. Prefer the builder's LIVE rules over the
  // engine's last-run snapshot so dragging the threshold slider updates the chart in real time.
  const patternRules = liveRules || engine.rules || {};
  const patternLongSets = Array.isArray(patternRules.patternLongTemplates) ? patternRules.patternLongTemplates : [];
  const patternShortSets = Array.isArray(patternRules.patternShortTemplates) ? patternRules.patternShortTemplates : [];
  const patternMode = patternRules.patternMatchMode === 'ohlc' ? 'ohlc' : 'close';
  const patternSimilarity = patternRules.patternSimilarity === 'dtw' ? 'dtw' : 'correlation';
  const patternThreshold = Number(patternRules.patternThreshold ?? 0.85);
  const patternWindows = useMemo(() => {
    const out = [];
    if (!patternRules.patternEntryEnabled) return out;
    const scan = (sets, side) => {
      if (!Array.isArray(sets) || !sets.length) return;
      for (let t = start; t < end; t += 1) {
        const bars = patternMatchAnyLength(engine.candles, t, sets, patternMode, patternSimilarity, patternThreshold);
        if (bars > 0) out.push({ lo: t - bars + 1, hi: t, side });
      }
    };
    scan(patternLongSets, 'long');
    scan(patternShortSets, 'short');
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.candles, start, end, patternRules.patternEntryEnabled, patternMode, patternSimilarity, patternThreshold, JSON.stringify(patternLongSets), JSON.stringify(patternShortSets)]);

  // Conditions-by-Example live preview: the signal candles matched by the capture currently
  // being tuned (strictness slider), plus the original selection so the user can see their
  // example always stays matched.
  const previewMarks = useMemo(() => {
    if (!conditionPreview || !Array.isArray(conditionPreview.indices)) return null;
    return conditionPreview.indices
      .filter((index) => index >= start && index < end)
      .map((index) => ({ index, side: conditionPreview.side === 'short' ? 'short' : 'long' }));
  }, [conditionPreview, start, end]);
  const previewSelection = conditionPreview?.selection || null;

  const patternIndexFromEvent = (event) => {
    const rect = pricePaneRef.current?.getBoundingClientRect();
    return getIndexFromClientX(event.clientX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);
  };
  const handlePatternPointerDown = (event) => {
    if (!selectMode) return;
    event.stopPropagation();
    event.preventDefault();
    const idx = patternIndexFromEvent(event);
    if (idx === null || idx === undefined) return;
    patternDragActiveRef.current = true;
    setPatternDrag({ lo: idx, hi: idx });
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* noop */ }
  };
  const handlePatternPointerMove = (event) => {
    if (!patternDragActiveRef.current) return;
    event.stopPropagation();
    const idx = patternIndexFromEvent(event);
    if (idx === null || idx === undefined) return;
    setPatternDrag((prev) => (prev ? { lo: prev.lo, hi: idx } : { lo: idx, hi: idx }));
  };
  const handlePatternPointerUp = (event) => {
    if (!patternDragActiveRef.current) return;
    event.stopPropagation();
    patternDragActiveRef.current = false;
    setPatternDrag((prev) => {
      if (prev) {
        const lo = clamp(Math.min(prev.lo, prev.hi), 0, totalCandles - 1);
        const hi = clamp(Math.max(prev.lo, prev.hi), 0, totalCandles - 1);
        if (hi - lo + 1 >= 2 && typeof onRangeSelected === 'function') {
          onRangeSelected(lo, hi);
        }
      }
      return null;
    });
  };


  const livePlayhead = clamp(currentBar, start, lastDataIndex);
  const playhead = clamp(followLive ? currentBar : markerIndex, start, lastDataIndex);
  const playheadX = xForIndex(playhead);
  const crosshairIndex = clamp(hoveredIndex ?? playhead, start, lastDataIndex);
  const showCrosshair = hoveredIndex !== null && hoveredIndex >= start && hoveredIndex <= lastDataIndex;
  const focusCandle = engine.candles[crosshairIndex];
  const readingsForOscillator = (oscillatorEntry) => oscillatorEntry.series.map((series) => ({
    id: series.id,
    label: series.label,
    color: series.color,
    value: series.values?.[crosshairIndex],
  }));
  const focusOscillatorReadings = safeOscillators.flatMap(readingsForOscillator);
  const currentPrice = engine.candles[livePlayhead]?.close ?? focusCandle.close;
  const currentPriceY = yForPrice(currentPrice);
  const crosshairPrice = showCrosshair ? getValueFromY(priceCrossY, priceMin, priceMax, PRICE_HEIGHT, PRICE_PADDING) : currentPrice;
  const overlayReadings = safeOverlays.flatMap((overlay) => overlay.series.map((series) => ({
    id: series.id,
    label: series.label,
    color: series.color,
    value: series.values?.[crosshairIndex],
  })));
  const isFollowingLiveBar = followLive && safePanOffset === 0;
  useEffect(() => {
    if (!canRequestOlder || isRequestingOlder || typeof onRequestOlder !== 'function' || !visibleCandles.length) {
      return;
    }

    if (safePanOffset < maxPanOffset - 1) {
      olderRequestKeyRef.current = null;
      return;
    }

    const anchorKey = `${visibleCandles[0].time}:${totalCandles}:${maxPanOffset}`;
    if (olderRequestKeyRef.current === anchorKey) {
      return;
    }
    olderRequestKeyRef.current = anchorKey;
    onRequestOlder();
  }, [canRequestOlder, isRequestingOlder, maxPanOffset, onRequestOlder, safePanOffset, totalCandles, visibleCandles]);

  function updateHoverFromPrice(event) {
    const rect = pricePaneRef.current?.getBoundingClientRect();
    const nextIndex = getIndexFromClientX(event.clientX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);
    setHoveredIndex(nextIndex);
    setPriceCrossY(getViewBoxY(event.clientY, rect, PRICE_HEIGHT));
  }

  function updateHoverFromOscillator(event) {
    const rect = oscillatorPaneRef.current?.getBoundingClientRect();
    const nextIndex = getIndexFromClientX(event.clientX, rect, start, end, OSCILLATOR_WIDTH, OSCILLATOR_PADDING);
    setHoveredIndex(nextIndex);
    setPriceCrossY(null);
  }

  const isInPriceAxis = useCallback((clientX) => {
    const rect = pricePaneRef.current?.getBoundingClientRect();
    return Boolean(rect && clientX >= rect.right - PRICE_AXIS_GUTTER);
  }, []);
  const isNearPlayhead = useCallback((clientX) => {
    const rect = pricePaneRef.current?.getBoundingClientRect();
    if (!rect) {
      return false;
    }
    const playheadClientX = rect.left + ((playheadX / PRICE_WIDTH) * rect.width);
    return Math.abs(clientX - playheadClientX) <= 12;
  }, [playheadX]);

  const zoomChart = useCallback((nextVisibleBars, anchorIndex) => {
    const clampedVisibleBars = clamp(nextVisibleBars, minVisibleBars, maxVisibleBars);
    const anchorRatio = clamp((anchorIndex - start) / Math.max(1, visibleBars - 1), 0, 1);
    const projectedEnd = Math.round(anchorIndex + 1 + ((1 - anchorRatio) * (clampedVisibleBars - 1)));
    const boundedEnd = clamp(projectedEnd, Math.min(clampedVisibleBars, liveEnd), liveEnd + Math.max(0, clampedVisibleBars - 12));
    const nextMaxPanOffset = Math.max(0, liveEnd - Math.min(clampedVisibleBars, liveEnd));
    const nextPanOffset = clamp(liveEnd - boundedEnd, -Math.max(0, clampedVisibleBars - 12), nextMaxPanOffset);
    setVisibleBars(clampedVisibleBars);
    setPanOffset(nextPanOffset);
  }, [liveEnd, maxVisibleBars, minVisibleBars, start, visibleBars]);

  const scalePrice = useCallback((factor) => {
    setPriceScale((previous) => clamp(previous * factor, MIN_PRICE_SCALE, MAX_PRICE_SCALE));
  }, []);

  const panByPixels = useCallback((pixelDelta) => {
    const width = pricePaneRef.current?.getBoundingClientRect().width ?? 1;
    const barsPerPixel = Math.max(1, visibleBars - 1) / Math.max(width, 1);
    setPanOffset((previous) => clamp(previous + Math.round(pixelDelta * barsPerPixel), -Math.max(0, visibleBars - 12), maxPanOffset));
  }, [maxPanOffset, visibleBars]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pricePane = pricePaneRef.current;
    const oscillatorPane = oscillatorPaneRef.current;
    const chartRoot = chartRootRef.current;
    if (!pricePane || !chartRoot) {
      return undefined;
    }

    const wheelListener = (event) => {
      const rect = pricePaneRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      // Never hijack scrolling that belongs to the floating setup/results panels or any
      // other overlay sitting above the chart panes.
      if (typeof event.target?.closest === 'function' && event.target.closest('.tester-panel, .tester-capture-dock, .tester-chart-overlay')) {
        return;
      }

      event.preventDefault();
      const anchorIndex = getIndexFromClientX(event.clientX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);

      if (event.ctrlKey || event.metaKey) {
        const deltaBars = Math.max(6, Math.round(visibleBars * 0.12));
        const nextVisibleBars = event.deltaY < 0 ? visibleBars - deltaBars : visibleBars + deltaBars;
        zoomChart(nextVisibleBars, anchorIndex);
        return;
      }

      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) * 0.65) {
        panByPixels(event.deltaX);
        return;
      }

      if (Math.abs(event.deltaY) > 0) {
        scalePrice(event.deltaY < 0 ? 1.08 : 0.92);
      }
    };
    const preventGesture = (event) => event.preventDefault();

    pricePane.addEventListener('wheel', wheelListener, { passive: false });
    oscillatorPane?.addEventListener('wheel', wheelListener, { passive: false });
    chartRoot.addEventListener('gesturestart', preventGesture);
    chartRoot.addEventListener('gesturechange', preventGesture);
    chartRoot.addEventListener('gestureend', preventGesture);

    return () => {
      pricePane.removeEventListener('wheel', wheelListener);
      oscillatorPane?.removeEventListener('wheel', wheelListener);
      chartRoot.removeEventListener('gesturestart', preventGesture);
      chartRoot.removeEventListener('gesturechange', preventGesture);
      chartRoot.removeEventListener('gestureend', preventGesture);
    };
  }, [end, isInPriceAxis, liveEnd, panByPixels, scalePrice, start, visibleBars, zoomChart]);

  function rememberTouchPointer(event) {
    if (event.pointerType !== 'touch') {
      return;
    }
    touchPointsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  }

  function forgetTouchPointer(pointerId) {
    touchPointsRef.current.delete(pointerId);
    if (touchPointsRef.current.size < 2) {
      touchGestureRef.current = null;
    }
  }

  function applyTouchGesture() {
    if (touchPointsRef.current.size < 2) {
      return;
    }

    const rect = pricePaneRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const [a, b] = Array.from(touchPointsRef.current.values()).slice(0, 2);
    const centerX = (a.x + b.x) / 2;
    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    const ySpan = Math.max(8, Math.abs(a.y - b.y));
    const gesture = touchGestureRef.current;

    if (gesture) {
      panByPixels(gesture.centerX - centerX);

      if (gesture.distance > 0) {
        const zoomFactor = distance / gesture.distance;
        if (Math.abs(zoomFactor - 1) > 0.014) {
          const anchorIndex = getIndexFromClientX(centerX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);
          zoomChart(visibleBars / zoomFactor, anchorIndex);
        }
      }

      if (gesture.ySpan > 0) {
        const priceFactor = ySpan / gesture.ySpan;
        if (Math.abs(priceFactor - 1) > 0.014) {
          scalePrice(priceFactor);
        }
      }
    }

    touchGestureRef.current = { centerX, distance, ySpan };
  }

  function handleSurfacePointerDown(event) {
    rememberTouchPointer(event);
    if (touchPointsRef.current.size >= 2) {
      event.preventDefault();
      panStateRef.current = null;
      priceScaleStateRef.current = null;
      setIsPanning(false);
      setIsScalingPrice(false);
      applyTouchGesture();
      return;
    }

    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }

    event.preventDefault();
    onChartFocus?.();

    if (event.currentTarget === pricePaneRef.current) {
      updateHoverFromPrice(event);
    }

    if (event.currentTarget === pricePaneRef.current && !followLive && isNearPlayhead(event.clientX)) {
      markerDragStateRef.current = {
        pointerId: event.pointerId,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setIsDraggingMarker(true);
      return;
    }

    if (isInPriceAxis(event.clientX)) {
      priceScaleStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScale: priceScale,
        mode: 'pending',
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      return;
    }

    const rect = pricePaneRef.current?.getBoundingClientRect();
    panStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      startPanOffset: safePanOffset,
      width: rect?.width ?? 1,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsPanning(true);
  }

  function handlePricePointerMove(event) {
    if (event.pointerType === 'touch' && touchPointsRef.current.has(event.pointerId)) {
      touchPointsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (touchPointsRef.current.size >= 2) {
        event.preventDefault();
        applyTouchGesture();
        return;
      }
    }

    event.preventDefault();
    updateHoverFromPrice(event);

    if (markerDragStateRef.current?.pointerId === event.pointerId) {
      const rect = pricePaneRef.current?.getBoundingClientRect();
      if (rect) {
        const chartRightEdge = rect.right - ((PRICE_AXIS_GUTTER / PRICE_WIDTH) * rect.width);
        let nextIndex = getIndexFromClientX(event.clientX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);
        if (event.clientX <= rect.left + 28 && safePanOffset < maxPanOffset) {
          setPanOffset((previous) => clamp(previous + 1, 0, maxPanOffset));
          nextIndex = Math.max(0, start - 1);
        } else if (event.clientX >= chartRightEdge - 20 && safePanOffset > 0) {
          setPanOffset((previous) => clamp(previous - 1, 0, maxPanOffset));
          nextIndex = Math.min(liveEnd - 1, end);
        }
        setMarkerIndex(nextIndex);
      }
      return;
    }

    if (priceScaleStateRef.current?.pointerId === event.pointerId) {
      const deltaX = event.clientX - priceScaleStateRef.current.startX;
      const deltaY = priceScaleStateRef.current.startY - event.clientY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (priceScaleStateRef.current.mode === 'pending') {
        if (absY >= PRICE_SCALE_DRAG_THRESHOLD && absY > absX) {
          priceScaleStateRef.current = {
            ...priceScaleStateRef.current,
            mode: 'scaling',
          };
          setIsScalingPrice(true);
        } else if (absX >= PRICE_SCALE_DRAG_THRESHOLD && absX >= absY) {
          const rect = pricePaneRef.current?.getBoundingClientRect();
          panStateRef.current = {
            pointerId: event.pointerId,
            startX: priceScaleStateRef.current.startX,
            startPanOffset: safePanOffset,
            width: rect?.width ?? 1,
          };
          priceScaleStateRef.current = null;
          setIsPanning(true);
        } else {
          return;
        }
      }

      if (priceScaleStateRef.current?.mode === 'scaling') {
        const factor = clamp(1 + (deltaY / 240), 0.55, 2.2);
        setPriceScale(clamp(priceScaleStateRef.current.startScale * factor, MIN_PRICE_SCALE, MAX_PRICE_SCALE));
        return;
      }
    }

    if (panStateRef.current?.pointerId !== event.pointerId) {
      return;
    }
    const barsPerPixel = Math.max(1, visibleBars - 1) / Math.max(1, panStateRef.current.width);
    const pixelDelta = panStateRef.current.startX - event.clientX;
    panStateRef.current.moved = panStateRef.current.moved || Math.abs(pixelDelta) > CLICK_DRAG_THRESHOLD || Math.abs((panStateRef.current.startY ?? event.clientY) - event.clientY) > CLICK_DRAG_THRESHOLD;
    const barShift = Math.round(pixelDelta * barsPerPixel);
    setPanOffset(clamp(panStateRef.current.startPanOffset + barShift, -Math.max(0, visibleBars - 12), maxPanOffset));
  }

  function handleOscillatorPointerMove(event) {
    if (event.pointerType === 'touch' && touchPointsRef.current.has(event.pointerId)) {
      touchPointsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (touchPointsRef.current.size >= 2) {
        event.preventDefault();
        applyTouchGesture();
        return;
      }
    }

    event.preventDefault();
    updateHoverFromOscillator(event);

    if (panStateRef.current?.pointerId !== event.pointerId) {
      return;
    }
    const barsPerPixel = Math.max(1, visibleBars - 1) / Math.max(1, panStateRef.current.width);
    const pixelDelta = panStateRef.current.startX - event.clientX;
    panStateRef.current.moved = panStateRef.current.moved || Math.abs(pixelDelta) > CLICK_DRAG_THRESHOLD || Math.abs((panStateRef.current.startY ?? event.clientY) - event.clientY) > CLICK_DRAG_THRESHOLD;
    const barShift = Math.round(pixelDelta * barsPerPixel);
    setPanOffset(clamp(panStateRef.current.startPanOffset + barShift, -Math.max(0, visibleBars - 12), maxPanOffset));
  }

  function endSurfacePointer(event) {
    forgetTouchPointer(event.pointerId);

    if (markerDragStateRef.current?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      markerDragStateRef.current = null;
      setIsDraggingMarker(false);
    }

    if (priceScaleStateRef.current?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      priceScaleStateRef.current = null;
      setIsScalingPrice(false);
    }

    if (panStateRef.current?.pointerId === event.pointerId) {
      const rect = event.currentTarget === pricePaneRef.current
        ? pricePaneRef.current?.getBoundingClientRect()
        : oscillatorPaneRef.current?.getBoundingClientRect();
      const wasClick = !panStateRef.current.moved;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      panStateRef.current = null;
      setIsPanning(false);
      // Inspect ("why?") mode: a plain click — drags still pan — asks the debugger to
      // explain the clicked candle. Runs through the normal pan/click path so hover,
      // panning, zooming, and the toolbar all keep working while inspecting.
      if (wasClick && rect && inspectMode && typeof onBarInspect === 'function' && event.currentTarget === pricePaneRef.current) {
        const inspectIndex = getIndexFromClientX(event.clientX, rect, start, end, PRICE_WIDTH, PRICE_PADDING);
        if (inspectIndex !== null && inspectIndex !== undefined) {
          onBarInspect(clamp(inspectIndex, 0, totalCandles - 1));
        }
      }
      if (wasClick && !followLive && rect) {
        const nextIndex = getIndexFromClientX(
          event.clientX,
          rect,
          start,
          end,
          event.currentTarget === pricePaneRef.current ? PRICE_WIDTH : OSCILLATOR_WIDTH,
          event.currentTarget === pricePaneRef.current ? PRICE_PADDING : OSCILLATOR_PADDING,
        );
        setMarkerIndex(nextIndex);
      }
    }
  }

  function handleSplitterPointerDown(event) {
    if (event.button !== 0) {
      return;
    }
    const chartRect = chartRootRef.current?.getBoundingClientRect();
    const maxHeight = Math.min(
      MAX_OSCILLATOR_HEIGHT * Math.max(1, oscillatorCount),
      Math.max(MIN_OSCILLATOR_HEIGHT, (chartRect?.height ?? 0) - 120),
    );
    resizeStateRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: oscillatorHeight,
      maxHeight,
    };
    splitterRef.current?.setPointerCapture?.(event.pointerId);
    setIsResizingOscillator(true);
  }

  function handleSplitterPointerMove(event) {
    if (resizeStateRef.current?.pointerId !== event.pointerId) {
      return;
    }
    const delta = resizeStateRef.current.startY - event.clientY;
    const nextHeight = clamp(
      resizeStateRef.current.startHeight + delta,
      MIN_OSCILLATOR_HEIGHT,
      resizeStateRef.current.maxHeight,
    );
    setOscillatorHeight(nextHeight <= OSCILLATOR_COLLAPSE_THRESHOLD ? 0 : nextHeight);
  }

  function endSplitterResize(event) {
    if (resizeStateRef.current?.pointerId !== event.pointerId) {
      return;
    }
    splitterRef.current?.releasePointerCapture?.(event.pointerId);
    resizeStateRef.current = null;
    setIsResizingOscillator(false);
  }

  function clearHover() {
    if (isPanning || isDraggingMarker || isScalingPrice || isResizingOscillator || touchPointsRef.current.size) {
      return;
    }
    setHoveredIndex(null);
    setPriceCrossY(null);
  }

  return (
    <div ref={chartRootRef} className="tester-interactive-chart" style={{ gridTemplateRows: oscillatorVisible ? `minmax(0, 1fr) 12px ${oscillatorHeight}px` : 'minmax(0, 1fr)' }}>
      <div className="tester-chart-overlay">
        <div className={`tester-chart-readout ${readoutCollapsed ? 'is-collapsed' : ''}`}>
          <div className="tester-chart-readout-head">
            <strong>{formatDateTime(focusCandle.time)}</strong>
            <div className="tester-chart-readout-actions">
              <span>{showCrosshair ? 'Hover' : followLive ? 'Live' : 'Marker'}</span>
              <button type="button" className="tester-chart-control tester-chart-control-icon" onClick={() => setReadoutCollapsed((previous) => !previous)} aria-label={readoutCollapsed ? 'Expand chart info' : 'Collapse chart info'} title={readoutCollapsed ? 'Expand' : 'Collapse'}>
                {readoutCollapsed ? <FiChevronDown /> : <FiChevronUp />}
              </button>
            </div>
          </div>
          {!readoutCollapsed && (
            <>
              <div className="tester-chart-readout-row">
                <span>O {formatPrice(focusCandle.open, engine.profile)}</span>
                <span>H {formatPrice(focusCandle.high, engine.profile)}</span>
                <span>L {formatPrice(focusCandle.low, engine.profile)}</span>
                <span>C {formatPrice(focusCandle.close, engine.profile)}</span>
              </div>
              <div className="tester-chart-readout-row">
                {overlayReadings.map((overlay) => (
                  <span key={overlay.id} className="tester-chart-chip" style={{ '--chip-color': overlay.color }}>
                    {overlay.label} {isValidValue(overlay.value) ? formatPrice(overlay.value, engine.profile) : '--'}
                  </span>
                ))}
                {focusOscillatorReadings.map((series) => (
                  <span key={series.id} className="tester-chart-chip" style={{ '--chip-color': series.color }}>
                    {series.label} {isValidValue(series.value) ? formatIndicatorNumber(series.value) : '--'}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="tester-chart-controls">
          <button
            type="button"
            className="tester-chart-control tester-chart-control-icon"
            onClick={() => zoomChart(visibleBars - Math.max(6, Math.round(visibleBars * 0.12)), crosshairIndex)}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <FiZoomIn />
          </button>
          <button
            type="button"
            className="tester-chart-control tester-chart-control-icon"
            onClick={() => zoomChart(visibleBars + Math.max(6, Math.round(visibleBars * 0.12)), crosshairIndex)}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <FiZoomOut />
          </button>
          <button type="button" className="tester-chart-control tester-chart-control-scale" onClick={() => scalePrice(1.12)} aria-label="Increase price scale" title="Increase candle height">
            +Y
          </button>
          <button type="button" className="tester-chart-control tester-chart-control-scale" onClick={() => scalePrice(0.9)} aria-label="Decrease price scale" title="Decrease candle height">
            -Y
          </button>
          <button type="button" className={`tester-chart-control tester-chart-control-icon ${showTradeVisuals ? 'is-active' : ''}`} onClick={() => setShowTradeVisuals((previous) => !previous)} aria-label={showTradeVisuals ? 'Hide entry and exit visuals' : 'Show entry and exit visuals'} title={showTradeVisuals ? 'Trade visuals on' : 'Trade visuals off'}>
            {showTradeVisuals ? <FiEye /> : <FiEyeOff />}
          </button>
          {typeof onBarInspect === 'function' && (
            <button
              type="button"
              className={`tester-chart-control tester-chart-control-icon ${inspectMode ? 'is-active' : ''}`}
              onClick={() => setInspectMode((previous) => !previous)}
              aria-label={inspectMode ? 'Exit explain-bar mode' : 'Explain a bar: why did or didn\'t it trade?'}
              title={inspectMode ? 'Explain mode on — click a candle' : 'Why did/didn\'t it trade here?'}
            >
              <FiHelpCircle />
            </button>
          )}
          <button
            type="button"
            className={`tester-chart-control tester-chart-control-icon ${followLive ? 'is-active' : ''}`}
            onClick={() => { if (!followLive) setPanOffset(0); setFollowLive((previous) => !previous); }}
            aria-label={followLive ? 'Disable auto-follow' : 'Enable auto-follow'}
            title={followLive ? 'Auto-follow on' : 'Auto-follow off'}
          >
            {followLive ? <FiLock /> : <FiUnlock />}
          </button>
          <button type="button" className={`tester-chart-control ${isFollowingLiveBar ? 'is-active' : ''}`} onClick={() => { setFollowLive(true); setMarkerIndex(currentBar); setPanOffset(0); }}>
            <FiCrosshair /> Live
          </button>
        </div>
      </div>

      <div
        ref={pricePaneRef}
        className={`tester-price-pane ${isPanning ? 'is-panning' : ''} ${isScalingPrice ? 'is-scaling-price' : ''}`}
        onPointerDown={handleSurfacePointerDown}
        onPointerMove={handlePricePointerMove}
        onPointerUp={endSurfacePointer}
        onPointerCancel={endSurfacePointer}
        onPointerLeave={clearHover}
      >
        <CandleChart
          chartType={chartType}
          engine={engine}
          start={start}
          end={end}
          min={priceMin}
          max={priceMax}
          yForPrice={yForPrice}
            xForIndex={xForIndex}
            visibleTrades={visibleTrades}
            visibleCancelledOrders={visibleCancelledOrders}
            visiblePendingOrders={visiblePendingOrders}
            currentBar={currentBar}
            playheadX={playheadX}
          crosshairIndex={crosshairIndex}
          priceCrossY={priceCrossY}
          showCrosshair={showCrosshair}
          focusCandle={focusCandle}
          crosshairPrice={crosshairPrice}
          currentPrice={currentPrice}
          currentPriceY={currentPriceY}
          showTradeVisuals={showTradeVisuals}
          liveFromIndex={liveFromIndex}
          liveFromTime={liveFromTime}
          patternDrag={patternDrag}
          patternWindows={patternWindows}
          previewMarks={previewMarks}
          previewSelection={previewSelection}
          inspectedIndex={inspectedIndex}
        />
        {selectMode && (
          <div
            className="tester-pattern-capture"
            onPointerDown={handlePatternPointerDown}
            onPointerMove={handlePatternPointerMove}
            onPointerUp={handlePatternPointerUp}
            onPointerCancel={handlePatternPointerUp}
          />
        )}
      </div>

      {oscillatorVisible && (
        <>
          <button
            ref={splitterRef}
            type="button"
            className={`tester-oscillator-splitter ${isResizingOscillator ? 'is-active' : ''} ${oscillatorHeight === 0 ? 'is-collapsed' : ''}`}
            aria-label="Resize oscillator pane"
            onPointerDown={handleSplitterPointerDown}
            onPointerMove={handleSplitterPointerMove}
            onPointerUp={endSplitterResize}
            onPointerCancel={endSplitterResize}
          />

          <div
            ref={oscillatorPaneRef}
            className={`tester-oscillator-pane ${oscillatorHeight === 0 ? 'is-collapsed' : ''}`}
            style={{ gridTemplateRows: `repeat(${Math.max(1, oscillatorCount)}, minmax(0, 1fr))` }}
            onPointerDown={handleSurfacePointerDown}
            onPointerMove={handleOscillatorPointerMove}
            onPointerUp={endSurfacePointer}
            onPointerCancel={endSurfacePointer}
            onPointerLeave={clearHover}
          >
            {oscillatorPanes.map((pane) => (
              <div key={pane.oscillator.id} className="tester-oscillator-subpane">
                <OscillatorChart
                  oscillator={pane.oscillator}
                  start={start}
                  end={end}
                  min={pane.min}
                  max={pane.max}
                  yForOscillator={pane.yForOscillator}
                  xForIndex={xForIndex}
                  crosshairIndex={crosshairIndex}
                  showCrosshair={showCrosshair}
                  focusOscillatorReadings={readingsForOscillator(pane.oscillator)}
                />
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
}






















